import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material'
import { AlbumPhoto } from '../../types'

export interface AlbumHeroProps {
  /** Full photo list for the album, already in display order. */
  photos: AlbumPhoto[]
  /** Owner-picked cover, matched by egoId — takes priority when present. */
  coverEgoId?: string
  /** Guest-side fallback: match the cover by URL when there's no egoId to compare against. */
  coverUrl?: string
  /** Owner is a fixed 380px banner; guest is taller (420px / 340px ≤640px) and its plate is centered. */
  variant: 'owner' | 'guest'
  /** Style override for the frosted metadata plate — e.g. centering it for the guest view. */
  plateSx?: SxProps<Theme>
  /**
   * Small floating badge (e.g. the "❤ N loved" chip) — rendered top-right,
   * independent of the plate. It used to live *inside* the plate, sharing a
   * row with the title/meta via `justifyContent: space-between` — that only
   * worked because the old plate spanned the full hero width. This plate is
   * a compact, content-sized card, so once title + meta + chip together got
   * close to its width, the chip wrapped onto its own line underneath,
   * making the card look oversized. Keeping it a separate corner badge
   * means the plate's height never depends on whether a chip happens to fit.
   */
  badge?: React.ReactNode
  /** Title / meta rendered inside the frosted plate. */
  children: React.ReactNode
}

const HeroRoot = styled(Box)({
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas, #0e0d12)',
})

// Same 4-stop Spectrum gradient already used for buttons/CTAs elsewhere,
// reused here any time there aren't enough photos to fill the hero on
// their own (0/1/2-photo albums). Keeps those states on-brand instead of
// a flat dark rectangle, without ever enlarging a photo past its native
// resolution.
const Mesh = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  background: [
    'radial-gradient(ellipse 46% 60% at 12% 20%, rgba(255,207,125,0.5), transparent 60%)',
    'radial-gradient(ellipse 50% 65% at 85% 15%, rgba(254,166,125,0.36), transparent 62%)',
    'radial-gradient(ellipse 55% 70% at 78% 92%, rgba(190,157,253,0.46), transparent 62%)',
    'radial-gradient(ellipse 40% 55% at 8% 88%, rgba(1,218,254,0.3), transparent 60%)',
    'var(--ds-night-canvas, #0e0d12)',
  ].join(', '),
})

const MeshDots = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1.2px, transparent 1.2px)',
  backgroundSize: '22px 22px',
})

const PrintRow = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 26,
})

const Print = styled(Box)({
  background: '#f4f1ea',
  padding: '10px 10px 26px',
  borderRadius: 4,
  boxShadow: '0 26px 54px rgba(0,0,0,0.45), 0 3px 8px rgba(0,0,0,0.28)',
  '& img': {
    display: 'block',
    width: '100%',
    aspectRatio: '3 / 2',
    objectFit: 'cover',
    borderRadius: 2,
  },
})

const MosaicLayer = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
})

const Tile = styled(Box)({
  position: 'relative',
  borderRadius: 13,
  overflow: 'hidden',
  boxShadow: '0 14px 32px rgba(0,0,0,0.4)',
  minWidth: 0,
  minHeight: 0,
  '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
})

const FiveWall = styled(Box)({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'grid',
  gridTemplateColumns: '1.15fr 1fr',
  gap: 10,
  padding: 22,
})

const BigTile = styled(Box)({
  position: 'relative',
  borderRadius: 14,
  overflow: 'hidden',
  boxShadow: '0 18px 40px rgba(0,0,0,0.5)',
  transform: 'rotate(-1deg)',
  minWidth: 0,
  minHeight: 0,
  '& img': { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
})

const QuadWrap = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gridTemplateRows: '1fr 1fr',
  gap: 10,
  minWidth: 0,
  minHeight: 0,
})

const MoreBadge = styled(Box)({
  position: 'absolute',
  bottom: 8,
  right: 8,
  zIndex: 5,
  background: 'rgba(10,9,13,0.75)',
  backdropFilter: 'blur(6px)',
  color: '#fff',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.06em',
  padding: '4px 9px',
  borderRadius: 999,
})

// Sits in the hero's own top-right corner — independent of the plate's
// width, so it never forces the plate to wrap/grow (see `badge` prop docs).
const CornerBadge = styled(Box)({
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 6,
})

// Frosted card instead of a bottom veil gradient — the old single-photo
// hero could rely on a dark gradient because there was always exactly one
// photo behind it; a mosaic, a mesh, or a pair of leaning prints can't
// guarantee the same contrast at the same corner every time, so the plate
// carries its own legibility regardless of what's behind it.
export const AlbumHeroPlate = styled(Box)({
  position: 'absolute',
  left: 24,
  bottom: 24,
  zIndex: 6,
  background: 'rgba(14,13,18,0.62)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 14,
  padding: '15px 19px',
  maxWidth: 460,
})

const THREE_ROTATE = [-1.2, 0.8, -0.6]
const FOUR_ROTATE = [-0.8, 0.6, 0.5, -0.7]
const QUAD_ROTATE = [0.8, -1.2, -0.9, 1.1]

function photoSrc(p?: AlbumPhoto) {
  return p?.url || p?.thumbUrl || ''
}

function pickLeadIndex(photos: AlbumPhoto[], coverEgoId?: string, coverUrl?: string) {
  if (coverEgoId) {
    const i = photos.findIndex((p) => p.egoId === coverEgoId)
    if (i >= 0) return i
  }
  if (coverUrl) {
    const i = photos.findIndex((p) => p.url === coverUrl || p.thumbUrl === coverUrl)
    if (i >= 0) return i
  }
  return 0
}

/**
 * The album hero used to ask a single photo to stretch full-bleed across a
 * ~1180px, 400px-tall banner — a normal phone photo (~720–1080px) isn't
 * captured at billboard resolution, so it visibly pixelated (issue #3).
 * Rather than fix that with one mosaic that assumes 5+ photos always exist
 * (leaving 0–4 photo albums to fall back to something flatter and less
 * finished), this reads `photos.length` and renders one of six states —
 * 0/1/2/3/4 each get their own honest layout, 5+ gets the full contact-
 * sheet mosaic. The seam moves from a product judgment call ("does this
 * album have enough photos?") to a simple array slice. Full design
 * rationale + every count mocked: docs/mockups-v23/01-album-adaptive-hero-full-page.html.
 */
export function AlbumHero({ photos, coverEgoId, coverUrl, variant, plateSx, badge, children }: AlbumHeroProps) {
  const ordered = useMemo(() => {
    const leadIdx = pickLeadIndex(photos, coverEgoId, coverUrl)
    if (leadIdx <= 0) return photos
    const copy = [...photos]
    const [lead] = copy.splice(leadIdx, 1)
    copy.unshift(lead)
    return copy
  }, [photos, coverEgoId, coverUrl])

  const count = ordered.length

  return (
    <HeroRoot
      sx={{
        height: variant === 'guest' ? 420 : 380,
        ...(variant === 'guest' ? { '@media (max-width: 640px)': { height: 340 } } : {}),
      }}
    >
      {count === 0 && (
        <>
          <Mesh />
          <MeshDots />
        </>
      )}

      {count === 1 && (
        <>
          <Mesh />
          <MeshDots />
          <PrintRow>
            <Print sx={{ width: 240, transform: 'rotate(-2deg)' }}>
              <img src={photoSrc(ordered[0])} alt='' />
            </Print>
          </PrintRow>
        </>
      )}

      {count === 2 && (
        <>
          <Mesh />
          <MeshDots />
          <PrintRow>
            <Print sx={{ width: 195, transform: 'rotate(-4deg)' }}>
              <img src={photoSrc(ordered[0])} alt='' />
            </Print>
            <Print sx={{ width: 195, transform: 'rotate(3deg)', mt: '14px' }}>
              <img src={photoSrc(ordered[1])} alt='' />
            </Print>
          </PrintRow>
        </>
      )}

      {count === 3 && (
        <MosaicLayer sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', padding: '30px 26px' }}>
          {ordered.slice(0, 3).map((p, i) => (
            <Tile key={p.id} sx={{ transform: `rotate(${THREE_ROTATE[i]}deg)`, mt: i === 1 ? '6px' : 0 }}>
              <img src={photoSrc(p)} alt='' />
            </Tile>
          ))}
        </MosaicLayer>
      )}

      {count === 4 && (
        <MosaicLayer
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '12px', padding: '26px' }}
        >
          {ordered.slice(0, 4).map((p, i) => (
            <Tile key={p.id} sx={{ transform: `rotate(${FOUR_ROTATE[i]}deg)` }}>
              <img src={photoSrc(p)} alt='' />
            </Tile>
          ))}
        </MosaicLayer>
      )}

      {count >= 5 && (
        <FiveWall>
          <BigTile>
            <img src={photoSrc(ordered[0])} alt='' />
            {count > 5 && <MoreBadge>+{count - 5}</MoreBadge>}
          </BigTile>
          <QuadWrap>
            {ordered.slice(1, 5).map((p, i) => (
              <Tile key={p.id} sx={{ transform: `rotate(${QUAD_ROTATE[i]}deg)` }}>
                <img src={photoSrc(p)} alt='' />
              </Tile>
            ))}
          </QuadWrap>
        </FiveWall>
      )}

      {badge && <CornerBadge>{badge}</CornerBadge>}
      <AlbumHeroPlate sx={plateSx}>{children}</AlbumHeroPlate>
    </HeroRoot>
  )
}
